'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import type { GetHistoryCategory, GetHistoryEntry, GetHistoryFilters } from '@repo/schema/history';
import { useDebouncedCallback } from '@repo/tanstack/pacer';
import { type InfiniteData, keepPreviousData, type QueryKey, useInfiniteQuery, type UseInfiniteQueryOptions, useQuery, type UseQueryOptions } from '@repo/tanstack/query';
import { Suspense, useEffect, useState } from 'react';
import { HistoryContent } from './history-content.tsx';
import { HistorySkeleton } from './history-skeleton.tsx';
import { flattenHistoryPages, type HistoryEntity } from './history-utils/index.ts';
import { loadHistoryEntity } from './load-history-entity.ts';
export type HistoryDataControllerProps = {
  entity: HistoryEntity;
  id: number;
  queryOptions: HistoryQueryOptionsFacade;
};
export type HistoryQueryOptionsFacade = {
  filters: (_id: number) => HistoryFiltersQueryOptions;
  list: (_input: HistoryQueryInput) => HistoryInfiniteQueryOptions;
};
export type LoadHistoryProps = {
  loadId: number;
};
type HistoryFiltersQueryOptions = Pick<UseQueryOptions<GetHistoryFilters, Error, GetHistoryFilters, QueryKey>, 'queryFn' | 'queryKey'>;
type HistoryInfiniteQueryOptions = Pick<UseInfiniteQueryOptions<HistoryPage, Error, InfiniteData<HistoryPage, number>, QueryKey, number>, 'getNextPageParam' | 'initialPageParam' | 'queryFn' | 'queryKey'>;
type HistoryPage = {
  data: GetHistoryEntry[];
  meta: {
    current_page: number;
    from?: null | number;
    last_page: number;
    per_page: number;
    to?: null | number;
    total?: null | number;
  };
};
type HistoryQueryInput = {
  category?: GetHistoryCategory;
  id: number;
  search?: string;
  userId?: number;
};
export const HistoryDataController = t0 => {
  const $ = _c(41);
  const {
    entity,
    id,
    queryOptions
  } = t0;
  const [category, setCategory] = useState();
  const [userId, setUserId] = useState();
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = value => setCommittedSearch(value.trim());
    t2 = {
      wait: 300
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const commitSearch = useDebouncedCallback(t1, t2);
  let t3;
  if ($[2] !== id || $[3] !== queryOptions) {
    t3 = queryOptions.filters(id);
    $[2] = id;
    $[3] = queryOptions;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      ...t3,
      throwOnError: true
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const filtersQuery = useQuery(t4);
  let t5;
  if ($[7] !== category || $[8] !== committedSearch || $[9] !== id || $[10] !== queryOptions || $[11] !== userId) {
    t5 = queryOptions.list({
      category,
      id,
      search: committedSearch || undefined,
      userId
    });
    $[7] = category;
    $[8] = committedSearch;
    $[9] = id;
    $[10] = queryOptions;
    $[11] = userId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = {
      ...t5,
      placeholderData: keepPreviousData,
      throwOnError: true
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const historyQuery = useInfiniteQuery(t6);
  let t7;
  if ($[15] !== historyQuery) {
    t7 = () => {
      if (historyQuery.hasNextPage && !historyQuery.isPlaceholderData && !historyQuery.isFetching && !historyQuery.isFetchingNextPage) {
        historyQuery.fetchNextPage();
      }
    };
    $[15] = historyQuery;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== historyQuery.fetchNextPage || $[18] !== historyQuery.hasNextPage || $[19] !== historyQuery.isFetching || $[20] !== historyQuery.isFetchingNextPage || $[21] !== historyQuery.isPlaceholderData) {
    t8 = [historyQuery.fetchNextPage, historyQuery.hasNextPage, historyQuery.isFetching, historyQuery.isFetchingNextPage, historyQuery.isPlaceholderData];
    $[17] = historyQuery.fetchNextPage;
    $[18] = historyQuery.hasNextPage;
    $[19] = historyQuery.isFetching;
    $[20] = historyQuery.isFetchingNextPage;
    $[21] = historyQuery.isPlaceholderData;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useEffect(t7, t8);
  if (filtersQuery.isPending || !filtersQuery.data || !historyQuery.data) {
    let t9;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <HistorySkeleton />;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    return t9;
  }
  let t9;
  if ($[24] !== historyQuery.data.pages) {
    t9 = flattenHistoryPages(historyQuery.data.pages);
    $[24] = historyQuery.data.pages;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const entries = t9;
  const queryIdentity = `${id}:${category ?? ""}:${userId ?? ""}:${committedSearch}`;
  const t10 = historyQuery.isFetching && !historyQuery.isFetchingNextPage;
  let t11;
  if ($[26] !== commitSearch) {
    t11 = value_0 => {
      setSearch(value_0);
      commitSearch(value_0);
    };
    $[26] = commitSearch;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== category || $[29] !== committedSearch || $[30] !== entity || $[31] !== entries || $[32] !== filtersQuery.data || $[33] !== historyQuery.isFetchingNextPage || $[34] !== historyQuery.isPlaceholderData || $[35] !== queryIdentity || $[36] !== search || $[37] !== t10 || $[38] !== t11 || $[39] !== userId) {
    t12 = <HistoryContent category={category} committedSearch={committedSearch} entity={entity} entries={entries} filters={filtersQuery.data} historyIdentity={queryIdentity} isFetchingNextPage={historyQuery.isFetchingNextPage} isFetchingResults={t10} isPlaceholderData={historyQuery.isPlaceholderData} onCategoryChange={setCategory} onSearchChange={t11} onUserChange={setUserId} search={search} userId={userId} data-sentry-element="HistoryContent" data-sentry-component="HistoryDataController" data-sentry-source-file="history.tsx" />;
    $[28] = category;
    $[29] = committedSearch;
    $[30] = entity;
    $[31] = entries;
    $[32] = filtersQuery.data;
    $[33] = historyQuery.isFetchingNextPage;
    $[34] = historyQuery.isPlaceholderData;
    $[35] = queryIdentity;
    $[36] = search;
    $[37] = t10;
    $[38] = t11;
    $[39] = userId;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
};
export const LoadHistory = t0 => {
  const $ = _c(6);
  const {
    loadId
  } = t0;
  const api = useApi();
  let t1;
  if ($[0] !== api) {
    t1 = {
      filters: id => api.loads.history.filters.queryOptions({
        path: {
          loadId: id
        }
      }),
      list: input => api.loads.history.list.queryOptions({
        input: {
          category: input.category,
          perPage: 50,
          search: input.search,
          userId: input.userId
        },
        path: {
          loadId: input.id
        }
      })
    };
    $[0] = api;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryOptions = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HistorySkeleton />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== loadId || $[4] !== queryOptions) {
    t3 = <Suspense fallback={t2} data-sentry-element="Suspense" data-sentry-component="LoadHistory" data-sentry-source-file="history.tsx"><HistoryDataController entity={loadHistoryEntity} id={loadId} key={loadId} queryOptions={queryOptions} data-sentry-element="HistoryDataController" data-sentry-source-file="history.tsx" /></Suspense>;
    $[3] = loadId;
    $[4] = queryOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};